/**
 * SMS review queue.
 *
 * Two mechanisms in the SMS system deliberately defer to a human, and until this page neither was
 * visible to anyone:
 *
 *   1. AMBIGUOUS INBOUND MESSAGES. "Please cancel my appointment" contains an opt-out keyword but
 *      probably is not one. We suppress the number anyway and flag it, because wrongly suppressing
 *      costs a member texts they wanted while wrongly not suppressing means texting someone who
 *      asked us to stop — only the second has legal consequence. Someone has to look.
 *
 *   2. POSSIBLY-SENT ATTEMPTS. If a worker's lease expires after it contacted the provider, we
 *      cannot know whether the message arrived, and nothing in Sinch deduplicates for us. Those
 *      attempts are never auto-retried; they wait here for a person to establish what happened.
 *
 * Resolving an inbound item does NOT lift the suppression. That is a separate, audited release by
 * an admin, because clearing a queue should never be the same gesture as re-subscribing someone.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  getReviewQueue,
  resolveInboundReview,
  resolveAttemptReview,
  type SmsReviewItem,
} from "../services/sms.service";

const classificationLabel: Record<string, string> = {
  possible_opt_out: "Possible opt-out",
  opt_in_request: "Asked to re-subscribe",
  unparseable_phone: "Unreadable sender",
  unparseable_payload: "Message had no body",
  possibly_dispatched: "Possibly sent",
};

const SmsReviewQueue: React.FC = () => {
  const [items, setItems] = useState<SmsReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getReviewQueue()
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, id: string) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      setNote((n) => ({ ...n, [id]: "" }));
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-1 text-2xl font-semibold text-gray-900">
        SMS review
      </div>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Messages the system could not decide on by itself. Ambiguous replies
        already stopped further texts to that number; resolving one here records
        your decision but does not re-subscribe anyone — an admin does that
        explicitly.
      </p>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          Nothing waiting. Ambiguous replies and uncertain sends will appear
          here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const id = `${it.kind}:${it.ref_id}`;
            const label =
              classificationLabel[it.classification ?? ""] ??
              it.classification ??
              it.kind;
            return (
              <div
                key={id}
                className="rounded-lg border border-gray-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      it.kind === "attempt"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {label}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {it.contact_name ?? "Unknown contact"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {it.phone_masked}
                  </span>
                  <span className="ml-auto text-xs text-gray-400">
                    {new Date(it.occurred_at).toLocaleString()}
                  </span>
                </div>

                <div className="mt-2 text-sm text-gray-700">{it.detail}</div>

                {it.body && (
                  <div className="mt-2 rounded bg-gray-50 px-3 py-2 text-sm text-gray-800">
                    <span className="whitespace-pre-wrap">{it.body}</span>
                  </div>
                )}

                <textarea
                  value={note[id] ?? ""}
                  onChange={(e) =>
                    setNote((n) => ({ ...n, [id]: e.target.value }))
                  }
                  rows={2}
                  placeholder="What did you decide, and how did you establish it? (required)"
                  className="mt-3 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="mt-2 flex flex-wrap justify-end gap-2">
                  {it.kind === "inbound" ? (
                    <button
                      type="button"
                      disabled={!note[id]?.trim() || busy === id}
                      onClick={() =>
                        act(
                          () =>
                            resolveInboundReview(it.ref_id, note[id]!.trim()),
                          id,
                        )
                      }
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      Mark reviewed
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={!note[id]?.trim() || busy === id}
                        onClick={() =>
                          act(
                            () =>
                              resolveAttemptReview(
                                it.ref_id,
                                "was_not_sent",
                                note[id]!.trim(),
                              ),
                            id,
                          )
                        }
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Confirmed it never reached the member — allow a retry"
                      >
                        It was not sent — allow retry
                      </button>
                      <button
                        type="button"
                        disabled={!note[id]?.trim() || busy === id}
                        onClick={() =>
                          act(
                            () =>
                              resolveAttemptReview(
                                it.ref_id,
                                "was_sent",
                                note[id]!.trim(),
                              ),
                            id,
                          )
                        }
                        className="rounded-lg bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:bg-gray-300"
                        title="The member received it — close the message with no further attempts"
                      >
                        It was sent — close it
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SmsReviewQueue;
